import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Form } from "react-bootstrap";
import { Check, Search, X } from "lucide-react";
import { RoleOption } from "./AccessControlService";

export interface RolePickerProps {
  roles: RoleOption[];
  selected: number[];
  onChange: (ids: number[]) => void;
  placeholder?: string;
  accent?: string;
  size?: "sm" | "md";
}

export const RolePicker: React.FC<RolePickerProps> = ({
  roles,
  selected,
  onChange,
  placeholder,
  accent = "#4f46e5",
  size = "md",
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const triggerRef = useRef<HTMLDivElement>(null);
  const [popupPos, setPopupPos] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
    openUp: boolean;
  } | null>(null);

  const filtered = useMemo(() => {
    if (!query.trim()) return roles;
    const q = query.toLowerCase();
    return roles.filter((r) => r.name.toLowerCase().includes(q));
  }, [roles, query]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const toggle = (id: number) => {
    if (selectedSet.has(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected, id]);
  };
  const selectedRoles = roles.filter((r) => selectedSet.has(r.id));

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const vh = window.innerHeight;
    const below = vh - rect.bottom - 12;
    const above = rect.top - 12;
    const preferredHeight = 320;
    const openUp = below < 200 && above > below;
    const maxHeight = Math.min(preferredHeight, Math.max(openUp ? above : below, 140));
    setPopupPos({
      top: openUp ? rect.top - maxHeight - 6 : rect.bottom + 6,
      left: rect.left,
      width: rect.width,
      maxHeight,
      openUp,
    });
  }, [open, query, filtered.length]);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [open]);

  const pad = size === "sm" ? "4px 8px" : "6px 10px";
  const minH = size === "sm" ? 36 : 44;

  return (
    <div style={{ position: "relative" }}>
      <div
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        style={{
          minHeight: minH,
          padding: pad,
          border: open ? `1.5px solid ${accent}` : "1.5px solid #e5e7eb",
          borderRadius: 8,
          background: "var(--surface-card)",
          cursor: "pointer",
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
          alignItems: "center",
          transition: "border-color 120ms, box-shadow 120ms",
          boxShadow: open ? `0 0 0 3px ${accent}20` : "none",
        }}
      >
        {selectedRoles.length === 0 && (
          <span style={{ color: "var(--text-faint)", fontSize: 13 }}>
            {placeholder ?? "All roles (click to restrict)"}
          </span>
        )}
        {selectedRoles.map((r) => (
          <span
            key={r.id}
            style={{
              background: `${accent}15`,
              color: accent,
              border: `1px solid ${accent}40`,
              borderRadius: 6,
              padding: "2px 8px",
              fontSize: 12,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontWeight: 500,
            }}
          >
            {r.name}
            <X
              size={12}
              style={{ cursor: "pointer", opacity: 0.8 }}
              onClick={(e) => {
                e.stopPropagation();
                toggle(r.id);
              }}
            />
          </span>
        ))}
      </div>
      {open && popupPos && createPortal(
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 2000 }} />
          <div
            style={{
              position: "fixed",
              top: popupPos.top,
              left: popupPos.left,
              width: popupPos.width,
              maxHeight: popupPos.maxHeight,
              background: "var(--surface-card)",
              border: "1px solid var(--border-default)",
              borderRadius: 10,
              boxShadow: "0 20px 48px rgba(15, 23, 42, 0.2)",
              zIndex: 2001,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div style={{ padding: 10, borderBottom: "1px solid var(--border-subtle)", background: "var(--surface-page)" }}>
              <div style={{ position: "relative" }}>
                <Search
                  size={14}
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: 10,
                    transform: "translateY(-50%)",
                    color: "var(--text-faint)",
                  }}
                />
                <Form.Control
                  size="sm"
                  autoFocus
                  placeholder="Search roles…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  style={{ paddingLeft: 30 }}
                />
              </div>
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              {filtered.length === 0 && (
                <div style={{ padding: 12, color: "var(--text-faint)", fontSize: 13, textAlign: "center" }}>
                  No roles match "{query}"
                </div>
              )}
              {filtered.map((r) => {
                const checked = selectedSet.has(r.id);
                return (
                  <div
                    key={r.id}
                    onClick={() => toggle(r.id)}
                    style={{
                      padding: "8px 12px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      background: checked ? `${accent}10` : "#fff",
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLDivElement).style.background = checked
                        ? `${accent}18`
                        : "#f9fafb";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLDivElement).style.background = checked
                        ? `${accent}10`
                        : "#fff";
                    }}
                  >
                    <div
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: 4,
                        border: checked ? `2px solid ${accent}` : "1.5px solid #d1d5db",
                        background: checked ? accent : "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {checked && <Check size={12} color="var(--text-on-brand)" strokeWidth={3} />}
                    </div>
                    <span style={{ fontSize: 14, color: "var(--text-strong)" }}>{r.name}</span>
                    <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text-faint)" }}>#{r.id}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </>,
        document.body,
      )}
    </div>
  );
};

export default RolePicker;
